import { proxy } from 'valtio';
import type { ArCellValue, ArFormulaEntry } from '#types';
import type { ArCell as ArCellType, CompositeFieldValue, UIElement } from '#types';

type CompositeFieldSidebarState = {
  isOpen: boolean;
  compositeFieldTypeId: string | null;
  entityId: string | null;
  fieldId: string | null;
  isFormulaColumn: boolean;
  tableMode: boolean;
};

export const compositeFieldSidebarState = proxy<CompositeFieldSidebarState>({
  isOpen: false,
  compositeFieldTypeId: null,
  entityId: null,
  fieldId: null,
  isFormulaColumn: false,
  tableMode: false,
});

/** Module-level vars — not proxied to avoid deep-proxying functions/complex objects. */
let cellValueProxy: ArCellType | null = null;
let currentOnChange: ((value: ArCellValue) => void) | undefined;
let currentValue: CompositeFieldValue | undefined;
let currentEntityStructure: UIElement | null = null;
let currentTitle: string | null = null;
let currentOnSwitchToReference: (() => void) | undefined;
let currentFormulaSequence: ArFormulaEntry[] | undefined;
let currentDerivedSubFieldReasons: ReadonlyMap<string, string> | undefined;

type CompositeFieldSidebarBaseParams = {
  cellValueProxy?: ArCellType;
  value?: CompositeFieldValue;
  onChange?: (value: ArCellValue) => void;
  fieldId: string;
  isFormulaColumn?: boolean;
  formulaSequence?: ArFormulaEntry[];
  /**
   * Sub-fields a Form Derive projection fills in the cell this sidebar edits,
   * each mapped to the sentence explaining the lock. They join the
   * formula-computed ones in the provider's disabled map, because both are
   * written by the platform and neither is the person's to edit.
   */
  derivedSubFieldReasons?: ReadonlyMap<string, string>;
  tableMode?: boolean;
  title?: string;
};

export type OpenCompositeFieldSidebarParams = CompositeFieldSidebarBaseParams & (
  | { compositeFieldTypeId: string; entityId?: never; entityStructure?: never }
  | { entityId: string; entityStructure?: UIElement; compositeFieldTypeId?: never; onSwitchToReference?: () => void }
);

export const openCompositeFieldSidebar = (params: OpenCompositeFieldSidebarParams): void => {
  cellValueProxy = params.cellValueProxy ?? null;
  currentOnChange = params.onChange;
  currentValue = params.value;
  currentEntityStructure = ('entityStructure' in params ? params.entityStructure : null) ?? null;
  currentTitle = params.title ?? null;
  currentOnSwitchToReference = ('onSwitchToReference' in params ? params.onSwitchToReference : undefined) ?? undefined;
  currentFormulaSequence = params.formulaSequence;
  currentDerivedSubFieldReasons = params.derivedSubFieldReasons;

  compositeFieldSidebarState.compositeFieldTypeId = params.compositeFieldTypeId ?? null;
  compositeFieldSidebarState.entityId = params.entityId ?? null;
  compositeFieldSidebarState.fieldId = params.fieldId;
  compositeFieldSidebarState.isFormulaColumn = params.isFormulaColumn ?? false;
  compositeFieldSidebarState.tableMode = params.tableMode ?? false;
  compositeFieldSidebarState.isOpen = true;
};

export const closeCompositeFieldSidebar = (): void => {
  compositeFieldSidebarState.isOpen = false;
  compositeFieldSidebarState.compositeFieldTypeId = null;
  compositeFieldSidebarState.entityId = null;
  compositeFieldSidebarState.fieldId = null;
  compositeFieldSidebarState.isFormulaColumn = false;
  compositeFieldSidebarState.tableMode = false;

  cellValueProxy = null;
  currentOnChange = undefined;
  currentValue = undefined;
  currentEntityStructure = null;
  currentTitle = null;
  currentOnSwitchToReference = undefined;
  currentFormulaSequence = undefined;
  currentDerivedSubFieldReasons = undefined;
};

export const getCellValueProxy = (): ArCellType | null => cellValueProxy;
export const getOnChange = (): ((value: ArCellValue) => void) | undefined => currentOnChange;
export const getCurrentValue = (): CompositeFieldValue | undefined => currentValue;
export const getEntityStructure = (): UIElement | null => currentEntityStructure;
export const getTitle = (): string | null => currentTitle;
export const getOnSwitchToReference = (): (() => void) | undefined => currentOnSwitchToReference;
export const getFormulaSequence = (): ArFormulaEntry[] | undefined => currentFormulaSequence;
export const getDerivedSubFieldReasons = (): ReadonlyMap<string, string> | undefined => currentDerivedSubFieldReasons;
